import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface TablePaginationState {
    currentPage: number;
    itemsPerPage: number;
    searchText: string;
}

export type HistoricalTableKey = 
    'surveyLoads' |
    'detailSurveyLoads' |
    'surveys' |
    'surveyParticipants' |
    'templates' |
    'campaigns' |
    'adminSurveys' |
    'historicalData' |
    'clients' |
    'events' |
    'participant' |
    'claim' |
    'user' |
    'claimReopenReason' |
    'demandInFormat' |
    'claimOrigin' |
    'claimProfessionalType' |
    'alertDeleteReason' |
    'claimDeleteReason' |
    'surveyClosedReason' |
    'improvementPlanType' |
    'improvementPlanOrigin' |
    'improvementPlanFinalizeReasons' |
    'improvementPlanDeleteReasons' |
    'functionalAreas' |
    'surveyPreviousClosed' |
    'serviceIndicators' |
    'alerts' |
    'auditLogs' |
    'sentimentAnalysis' |
    'roles' |
    'improvementPlans' |
    'improvementPlanDemandsModal' |
    'claimFollowUpType' |
    'claimCancelReason' |
    'demandDerivation' |
    'ips' |
    'demandOutFormat'|
    'claimPersonalRelationship' |
    'extraFields' |
    'notifications' |
    'claimRequestTypes' |
    'claimsForPersonByAffectedPeople' |
    'claimsForPersonByClaimantClaims' |
    'campaignClosedReason' |
    'userCommunicationTypes' |
    'professionals' |
    'reportDefinitions';

type HistoricalState = {
    [TableKey in HistoricalTableKey]: TablePaginationState;
}

const initialState: HistoricalState = {
    surveyLoads: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    detailSurveyLoads: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    surveys: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    surveyParticipants: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    templates: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    campaigns: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    adminSurveys: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    historicalData: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    clients: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    events: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    participant: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    claim: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    user: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    claimReopenReason: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    demandInFormat: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    claimOrigin: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    claimProfessionalType: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    claimDeleteReason: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    alertDeleteReason: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    surveyClosedReason: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    improvementPlanType: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    improvementPlanOrigin: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    improvementPlanFinalizeReasons: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    improvementPlanDeleteReasons: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    functionalAreas: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    surveyPreviousClosed: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    serviceIndicators: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    alerts: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    auditLogs: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    sentimentAnalysis: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    roles: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    improvementPlans: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    improvementPlanDemandsModal: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    claimFollowUpType: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    claimCancelReason: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    demandDerivation: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    ips: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    demandOutFormat: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    extraFields: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    claimPersonalRelationship: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    notifications: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    claimRequestTypes: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    claimsForPersonByAffectedPeople: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    claimsForPersonByClaimantClaims: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    campaignClosedReason: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    userCommunicationTypes: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    professionals: { currentPage: 1, itemsPerPage: 10, searchText: '' },
    reportDefinitions: { currentPage: 1, itemsPerPage: 10, searchText: '' }
};

const historicalSlice = createSlice({
    name: 'historical',
    initialState,
    reducers: {
        setCurrentPage: (
            state,
            action: PayloadAction<{ table: HistoricalTableKey; page: number }>
        ) => {
            const { table, page } = action.payload;
            if (state[table]) {
                state[table].currentPage = page;
            }
        },
        setItemsPerPage: (
            state,
            action: PayloadAction<{ table: HistoricalTableKey; size: number }>
        ) => {
            const { table, size } = action.payload;
            if (state[table]) {
                state[table].itemsPerPage = size;
                state[table].currentPage = 1; // resetea p�gina si cambia el tama�o
            }
        },
        setSearchText: (
            state,
            action: PayloadAction<{ table: HistoricalTableKey; text: string; goFirstPage?: boolean }> 
        ) => {
            const { table, text, goFirstPage = false } = action.payload;
            if (state[table]) {
                state[table].searchText = text;
                if (goFirstPage)
                    state[table].currentPage = 1;
            }
        }
    },
});

export const { setCurrentPage, setItemsPerPage, setSearchText } = historicalSlice.actions;
export default historicalSlice.reducer;
